/**
 * Shared visualization-filter expression builder.
 *
 * Both dataset viz (DatasetFilters → datasetPreview layers) and farm/block viz
 * (FarmFilters → blocksLayer) store filters as an array on vizSettings and
 * apply them to a Mapbox layer with setFilter(). This builds the Mapbox GL
 * filter expression from that array so the two paths stay consistent and both
 * support the same data types.
 *
 * Filter shapes:
 *   { header, type: 'number'|'date', min, max }   — range filter
 *   { header, type: 'category', values: [...] }   — keep only these values
 *
 * `header` is the GeoJSON property key (a string). For datasets that's the
 * column name; for blocks it's the block-field label (block features are
 * keyed by field label). createdOn/updatedOn are treated as numbers.
 *
 * Coercion notes:
 *   - Range uses ['to-number', ...] so numeric values stored as strings
 *     (e.g. data-logger "+16.1") still compare correctly.
 *   - Category uses ['to-string', ...] so the comparison is type-stable
 *     whether the stored value is a string or a number.
 *   - An empty category selection means "show nothing for this header" (the
 *     filter is explicit, not ignored) — represented as a literal false.
 */
function buildFilterExpression (filters) {
  if (!Array.isArray(filters) || !filters.length) return null
  const clauses = ['all']
  filters.forEach(filter => {
    if (!filter || !filter.header) return
    const key = filter.header
    if (filter.type === 'category') {
      const values = Array.isArray(filter.values) ? filter.values : []
      if (!values.length) {
        clauses.push(false) // explicit empty selection → match nothing
      } else {
        clauses.push(['in', ['to-string', ['get', key]], ['literal', values.map(v => String(v))]])
      }
    } else {
      // number / date range
      if (filter.min !== false && filter.min != null && filter.min !== '') {
        clauses.push(['>=', ['to-number', ['get', key]], Number(filter.min)])
      }
      if (filter.max !== false && filter.max != null && filter.max !== '') {
        clauses.push(['<=', ['to-number', ['get', key]], Number(filter.max)])
      }
    }
  })
  return clauses.length > 1 ? clauses : null
}

/**
 * Distinct non-empty values of a property across a FeatureCollection, for
 * populating the categorical value picker. Capped so a pathological column
 * can't render thousands of chips.
 */
function distinctValues (geojson, header, cap = 200) {
  const seen = new Set()
  const out = []
  const feats = (geojson && geojson.features) || []
  for (let i = 0; i < feats.length && out.length < cap; i++) {
    const props = feats[i] && feats[i].properties
    if (!props) continue
    const v = props[header]
    if (v === null || v === undefined || v === '') continue
    const s = String(v)
    if (!seen.has(s)) { seen.add(s); out.push(s) }
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/**
 * Decide whether a header reads as numeric across the data. Used by the
 * dataset filter UI (which has string headers and no declared type) to pick
 * the right control. A header is numeric only if EVERY non-empty value parses
 * as a finite number — one stray label makes it categorical.
 */
function inferHeaderType (geojson, header) {
  const feats = (geojson && geojson.features) || []
  let sawValue = false
  for (let i = 0; i < feats.length; i++) {
    const props = feats[i] && feats[i].properties
    if (!props) continue
    const v = props[header]
    if (v === null || v === undefined || v === '') continue
    sawValue = true
    if (!Number.isFinite(Number(v))) return 'category'
  }
  return sawValue ? 'number' : 'category'
}

module.exports = { buildFilterExpression, distinctValues, inferHeaderType }
